import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGlobalContext } from 'GlobalContext';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useSettings } from 'services/settings/SettingsContext';
import { saveData } from 'services/api/saveData';
import { formatTimelineDate, determineTaskDateClass } from 'services/utils/dateTimeUtils';
import { convertLocalDateTimeToUTC } from 'internationalization/timezoneConversions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faTrash } from '@fortawesome/free-solid-svg-icons';
import TaskCheckmark from 'assets/TaskCheckmark.svg';
import TaskForm from '../../views/tasks/TaskForm';
import { Task } from '../../views/tasks/TaskTypes';
import '../../style/scss/check-button.scss';
import '../../style/scss/tooltip.scss';

// A task as returned by the task list with its display fields
interface TicketTask extends Task {
    assignee_name?: string | null;
    due_date?: string | null;
    note_plain?: string | null;
}

interface TicketTaskCardProps {
    task: TicketTask;
    ticketId: number;
    onChanged: () => void;
}

// A task shown as card in the ticket conversation with check, edit and delete actions
const TicketTaskCard: React.FC<TicketTaskCardProps> = ({ task, ticketId, onChanged }) => {
    const { t } = useTranslation();
    const { userHash } = useAuthContext();
    const { setFloatingAlert } = useGlobalContext();
    const { userLocale, userTimezone } = useSettings();
    const [isEditing, setIsEditing] = useState(false);

    // Format the due date of the task
    const taskIsWholeDay = task.date !== null && task.start_datetime === null;
    const dueDate = task.due_date || task.start_datetime || task.date;
    const taskDate = dueDate ? formatTimelineDate(dueDate, taskIsWholeDay) : null;
    const dateClass = dueDate ? determineTaskDateClass(dueDate, taskIsWholeDay, task.checked) : '';

    // Toggle the checked state of the task
    const handleCheck = async () => {
        const utcDateTime = convertLocalDateTimeToUTC(new Date(), userLocale, userTimezone);
        try {
            const response = await saveData({
                apiUrl: `patch_task/${task.id}`,
                method: 'patch',
                data: {
                    checked: !task.checked,
                    checked_at: !task.checked ? utcDateTime : null,
                    checked_by_user_hash: !task.checked ? userHash : null,
                }
            });
            if (response?.status === 200) {
                setFloatingAlert({ type: 'success', message: !task.checked ? 'task.general.task_checked_alert' : 'task.general.task_unchecked_alert' });
                onChanged();
            }
        } catch (error) {
            console.error('Failed to update check state', error);
        }
    };

    // Soft delete the task
    const handleDelete = async () => {
        try {
            const response = await saveData({ apiUrl: `delete_task/${task.id}`, method: 'patch', data: {} });
            if (response?.status === 200) {
                setFloatingAlert({ type: 'success' });
                onChanged();
            }
        } catch (error) {
            setFloatingAlert({ type: 'danger', message: 'timeline.task.alert.delete_task_failed_message' });
        }
    };

    // Show the inline task form while editing
    if (isEditing) {
        return (
            <div className='ticket-task-card editing'>
                <TaskForm
                    placement='inline'
                    timeline_page='ticket'
                    timeline_page_id={ticketId}
                    taskId={task.id ?? undefined}
                    closeForm={() => setIsEditing(false)}
                    onSubmitSuccess={() => { setIsEditing(false); onChanged(); }}
                />
            </div>
        );
    }

    return (
        <div className={`ticket-task-card ${task.checked ? 'checked' : ''}`}>
            <div className='ticket-task-header'>
                <div className='check-button-wrapper'>
                    <button className={`check-button ${task.checked ? 'checked' : ''} tooltip-icon`} onClick={handleCheck}>
                        {task.checked ? <img src={TaskCheckmark} alt='Checked' /> : null}
                        <span className='tooltip'>{!task.checked ? t('timeline.task.general.mark_done_label') : t('timeline.task.general.mark_to_do_label')}</span>
                    </button>
                </div>
                <div className='ticket-task-title' onClick={() => setIsEditing(true)}>{task.subject}</div>
                <div className='ticket-task-actions'>
                    <div className='tooltip-icon edit-icon' onClick={() => setIsEditing(true)}>
                        <FontAwesomeIcon icon={faPen} />
                        <span className='tooltip'>{t('general.edit_label')}</span>
                    </div>
                    <div className='tooltip-icon delete-icon' onClick={handleDelete}>
                        <FontAwesomeIcon icon={faTrash} />
                        <span className='tooltip'>{t('general.delete')}</span>
                    </div>
                </div>
            </div>
            {task.note_plain && <p className='ticket-task-note'>{task.note_plain}</p>}
            <div className='ticket-message-meta'>
                <span className='task-label'>{t('ticket.conversation.task_label')}</span>
                {taskDate && <span className={dateClass}> · {taskDate}</span>}
                {task.assignee_name && <span> · {task.assignee_name}</span>}
            </div>
        </div>
    );
};

export default TicketTaskCard;
